import { AfterViewInit, Component, ElementRef, Signal, viewChild } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ProjectCadService } from '@project/components/import-export/cad-export/service/project-cad.service';
import { CadFormatVO } from '@article/components/data-center-export/cad-format.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { StringUtils } from '@shared/util/string-utils';
import { AlertHelper } from '@shared/alert/alert.helper';
import { UserSettings } from '@user/model/user-settings';
import { ProjectModel } from '@project/model/project.model';
import { VirtualScrollerComponent } from '@iharbeck/ngx-virtual-scroller';
import { CadProjectExportService } from '../service/cad-project-export.service';
import { UserModel } from '@user/model/user.model';
import { CadExportFeaturesDTO } from './cad-export-features.dto';
import { IsProjectType } from '@project/model/is-project-type';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { ProjectVO } from '@project/model/vo/project.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { TextureAreaUsageUtils } from '@shared/util/texture-area-usage-utils';

@Component({
  standalone: false,
  selector: 'cad-export',
  templateUrl: 'cad-export.component.html',
})
export class CadExportComponent implements AfterViewInit {
  private scroll: Signal<VirtualScrollerComponent | undefined> = viewChild('scroll');
  private container: Signal<ElementRef | undefined> = viewChild('container');

  public cadFormats: CadFormatVO[] = [];

  public filteredCadFormats: CadFormatVO[] = [];

  public selectedFormat?: CadFormatVO;

  public canExportWithLabeling: boolean = false;

  constructor(
    private bsModalRef: BsModalRef,
    private userSettings: UserSettings,
    private alertHelper: AlertHelper,
    private projectCadService: ProjectCadService,
    private projectModel: ProjectModel,
    private cadProjectExportService: CadProjectExportService,
    private userModel: UserModel,
  ) {}

  public ngAfterViewInit(): void {
    this.loadCadExportFeatures();
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  public export(): void {
    if (this.selectedFormat) {
      this.cadProjectExportService.exportProjectToCad(this.projectModel.currentProject, this.userModel.user.email, this.userModel.user, this.selectedFormat);
      this.close();
    }
  }

  public async exportWithLabeling(): Promise<void> {
    if (!this.selectedFormat || !this.canExportWithLabeling) return;
    await this.cadProjectExportService.exportProjectWithLabeling(this.projectModel.currentProject, this.selectedFormat);
    this.close();
  }

  public selectFormat(cadFormat: CadFormatVO): void {
    this.selectedFormat = cadFormat;
  }

  private loadCadExportFeatures(): void {
    this.projectCadService.getCadExportFeatures().subscribe({
      next: (cadExportFeatures: CadExportFeaturesDTO) => this.cadExportFeaturesLoaded(cadExportFeatures),
      error: (error) => this.loadCadExportFeatures_failure()
    });
  }

  private cadExportFeaturesLoaded(cadExportFeatures: CadExportFeaturesDTO): void {
    const cadFormats = cadExportFeatures.formats;
    cadFormats.sort((a: CadFormatVO, b: CadFormatVO) => StringUtils.stringCompare(a.name, b.name));
    this.cadFormats = cadFormats;
    this.filteredCadFormats = cadFormats;

    this.canExportWithLabeling = IsProjectType.mcsGds(this.projectModel.currentProject)
      && cadExportFeatures.canExportMcsGdsWithLabeling
      && this.hasNonEmptyTextAreaUsages(this.projectModel.currentProject);

    const formatValue: string = this.userSettings.selectedCadFormat;
    this.selectedFormat = this.findCadFormat(formatValue, this.cadFormats);
    if (this.selectedFormat && this.scroll) {
      if (this.container) {
        const nativeElement = this.container()?.nativeElement;
        // override return offsetTop from tbody element for corectly scrolling to selected item because 'ngx-virtual-scroller' used this property
        // and this property is not equal to 0 because 'fixedHeader' directive is used (it changes table element margin)
        Object.defineProperty(nativeElement, 'offsetTop', {
          get: function () {
            return nativeElement.scrollTop;
          },
        });
      }
      this.scroll()?.scrollToIndex(this.cadFormats.indexOf(this.selectedFormat));
    }
  }

  private hasNonEmptyTextAreaUsages(project: ProjectVO): boolean {
    return ProjectUtils.articleUsages(project)
      .flatMap((articleUsage: ArticleUsageVO) => ArticleUsageUtils.textureAreaUsages(articleUsage))
      .some(textureAreaUsage => !TextureAreaUsageUtils.isEmptyTextValue(textureAreaUsage.textValue));
  }

  private loadCadExportFeatures_failure(): void {
    this.alertHelper.showError('cadExportPopup.error.title.load_cad_formats', 'cadExportPopup.error.message.load_cad_formats');
  }

  public filterCadFormat(event: KeyboardEvent) {
    const text = (<any>event.target).value;
    this.filteredCadFormats = [];

    for (const cadFormat of this.cadFormats) {
      if (cadFormat.name.toLocaleLowerCase().indexOf(text.toLocaleLowerCase()) !== -1) {
        this.filteredCadFormats.push(cadFormat);
      }
    }
  }

  private findCadFormat(formatValue: string, cadFormats: CadFormatVO[]): CadFormatVO | undefined {
    if (!formatValue || ArrayUtils.isEmpty(cadFormats)) {
      return undefined;
    }
    for (const cadFormat of cadFormats) {
      if (cadFormat.format === formatValue) {
        return cadFormat;
      }
    }
    return undefined;
  }
}
